import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, onValue } from "firebase/database";
import { getFirebaseApp } from "../../lib/firebaseClient";
import { selectAdminToken, selectSessionId } from "../../admin/sessionSlice";
import { wmlGetState } from "../../admin/functions/wmlApi";
import { setBackButtonUrl } from "../uiSlice";
import { normalizeFirebaseList, normalizeWml } from "../../lib/wmlSnapshotUtils";
import leaderboardTextBackground from "../../img/assets/parts/leaderboard-text-background.png";
import trophyIcon from "../../img/assets/parts/trophy-icon.png";
import "./wmlLeaderboardPage.css";

const TERMINAL = new Set(["final", "ended"]);

export default function WmlLeaderboardPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, status: themeStatus } = useSelector(
    (state) => state.theme
  );

  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("waiting");
  const [resultsNorm, setResultsNorm] = useState(null);

  useEffect(() => {
    dispatch(setBackButtonUrl("/who-most-likely"));
  }, [dispatch]);

  useEffect(() => {
    if (!adminToken) {
      navigate("/login", { replace: true });
    }
  }, [adminToken, navigate]);

  useEffect(() => {
    if (
      !adminToken ||
      !currentTheme ||
      !sessionId ||
      themeStatus === "loading"
    ) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await wmlGetState({
          token: adminToken,
          currentTheme,
          sessionId,
        });
        if (cancelled || !data.success) return;
        setPhase(data.phase || "waiting");
        const norm = normalizeWml({ results: data.results });
        setResultsNorm(norm?.results ?? null);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, currentTheme, sessionId, themeStatus]);

  useEffect(() => {
    const app = getFirebaseApp();
    const sid = sessionId;
    if (!app || !sid || !process.env.REACT_APP_FIREBASE_DATABASE_URL) {
      return undefined;
    }
    const db = getDatabase(app);
    const wmlRef = ref(db, `/${sid}/wml`);
    const unsub = onValue(
      wmlRef,
      (snap) => {
        const n = normalizeWml(snap.val());
        if (!n?.phase) return;
        setPhase(n.phase);
        if (TERMINAL.has(n.phase)) {
          setResultsNorm(n.results ?? null);
        }
      },
      (err) => {
        console.warn("[WML LB] RTDB listener:", err?.message || err);
      }
    );
    return () => unsub();
  }, [sessionId]);

  useEffect(() => {
    if (loading || !currentTheme) return;
    if (!TERMINAL.has(phase)) {
      navigate("/who-most-likely", { replace: true });
    }
  }, [loading, phase, currentTheme, navigate]);

  const rows = useMemo(
    () => normalizeFirebaseList(resultsNorm?.leaderboard).slice(0, 8),
    [resultsNorm?.leaderboard]
  );

  if (!adminToken) {
    return null;
  }

  if (themeStatus === "loading" && !currentTheme) {
    return (
      <main className="wml-lb-page">
        <p className="wml-lb-status">Loading…</p>
      </main>
    );
  }

  if (!currentTheme) {
    navigate("/login", { replace: true });
    return null;
  }

  if (loading) {
    return (
      <main className="wml-lb-page">
        <p className="wml-lb-status">Loading…</p>
      </main>
    );
  }

  if (!TERMINAL.has(phase)) {
    return null;
  }

  return (
    <main className="wml-lb-page">
      <div className="wml-lb-card">
        <img
          src={trophyIcon}
          alt=""
          className="wml-lb-trophy wml-lb-trophy--left"
          aria-hidden
        />
        <img
          src={trophyIcon}
          alt=""
          className="wml-lb-trophy wml-lb-trophy--right"
          aria-hidden
        />
        <div className="wml-lb-banner">
          <img
            src={leaderboardTextBackground}
            alt="Leaderboard"
            className="wml-lb-banner__img"
          />
        </div>
        {rows.length === 0 ? (
          <p className="wml-lb-empty">No scores recorded yet.</p>
        ) : (
          <ul className="wml-lb-list">
            {rows.map((row, idx) => (
              <li key={row.playerId ?? idx} className="wml-lb-row">
                <span className="wml-lb-rank">{idx + 1}.</span>
                <span className="wml-lb-name">
                  {row.displayName || `Player ${row.playerId}`}
                </span>
                <span className="wml-lb-pts">{row.points ?? 0}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
